import { useMemo, useState } from 'react';
import { CheckCircle2, CircleDashed, AlertTriangle, Copy, ListChecks } from 'lucide-react';
import Button from '@/components/ui/Button';
import { useSettings } from '@/hooks/useProducts';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';
import {
  ASSET_SPECS,
  SURFACE_LABELS,
  brandContext,
  type AssetSpec,
  type AssetSurface,
} from '@/config/asset-specs';
import { SITE_NAME } from '@/config/site';
import Tooltip from '@/components/ui/Tooltip';

/**
 * Design Checklist — every image this site needs, at what size, and whether it
 * is there yet.
 *
 * The question this answers is the one an incoming designer asks first and the
 * platform previously could not answer: "what am I making, and how big?" The
 * sizes came from React props and the list of slots did not exist anywhere, so
 * it was a conversation rather than a page.
 *
 * "Filled" is best-effort and deliberately conservative — it reads the settings
 * a slot maps to and the homepage sections that exist. A slot that lives on a
 * section this site has not added shows as not applicable rather than missing,
 * so the count means something.
 */

interface HomepageSection {
  id: string;
  section_type: string;
  content: Record<string, unknown> | null;
}

type SlotState = 'filled' | 'empty' | 'not-applicable';

function useHomepageSections() {
  return useQuery({
    queryKey: ['homepage'],
    queryFn: () => api.get<{ sections: HomepageSection[] }>('/admin/homepage').then((r) => r.sections),
  });
}

/**
 * Resolve a slot against what the site actually has.
 *
 * Homepage slots are keyed `<section_type>.<field>`; everything else is a
 * `site_settings` key. Slots we cannot check (catalog and email images live per
 * row, not in one place) report not-applicable rather than guessing.
 */
function resolveState(
  spec: AssetSpec,
  settings: Record<string, unknown> | undefined,
  sections: HomepageSection[] | undefined,
): SlotState {
  if (spec.surface === 'homepage') {
    if (!sections) return 'not-applicable';
    const [sectionType, field] = spec.key.split('.');
    const matching = sections.filter((s) => s.section_type === sectionType);
    if (matching.length === 0) return 'not-applicable';
    const anyFilled = matching.some((s) => {
      const value = (s.content ?? {})[field];
      if (Array.isArray(value)) return value.length > 0;
      return typeof value === 'string' && value.trim() !== '';
    });
    return anyFilled ? 'filled' : 'empty';
  }

  if (spec.surface === 'catalog' || spec.surface === 'email') {
    // Per-row or per-template; there is no single value to inspect.
    return 'not-applicable';
  }

  const value = settings?.[spec.key];
  return typeof value === 'string' && value.trim() !== '' ? 'filled' : 'empty';
}

const STATE_META: Record<
  SlotState,
  { label: string; className: string; Icon: typeof CheckCircle2; help: string }
> = {
  filled: {
    label: 'Set',
    className: 'text-green-600',
    Icon: CheckCircle2,
    help: 'An image is in this slot. The checklist does not judge whether it is the RIGHT image, or whether it matches the size below — only that something is there.',
  },
  empty: {
    label: 'Not set',
    className: 'text-amber-600',
    Icon: AlertTriangle,
    help: 'Nothing in this slot. The storefront falls back to a placeholder or leaves the space empty, depending on where it appears.',
  },
  'not-applicable': {
    label: 'Not used here',
    className: 'text-gray-400',
    Icon: CircleDashed,
    help: 'This site does not use the section that needs this image, so it is nothing to fix. Adding that section to the homepage would make the slot apply.',
  },
};

export default function DesignChecklist() {
  const addToast = useUIStore((s) => s.addToast);
  const { data: settingsData } = useSettings();
  const { data: sections } = useHomepageSections();
  const [showAll, setShowAll] = useState(false);

  const settings = settingsData?.settings as Record<string, unknown> | undefined;

  const rows = useMemo(
    () => ASSET_SPECS.map((spec) => ({ spec, state: resolveState(spec, settings, sections) })),
    [settings, sections],
  );

  const visible = showAll ? rows : rows.filter((r) => r.state !== 'not-applicable');

  const grouped = useMemo(() => {
    const out = new Map<AssetSurface, typeof visible>();
    for (const row of visible) {
      const list = out.get(row.spec.surface) ?? [];
      list.push(row);
      out.set(row.spec.surface, list);
    }
    return out;
  }, [visible]);

  const missing = rows.filter((r) => r.state === 'empty' && r.spec.required).length;

  /** Plain-text spec sheet, so the list can leave the admin panel. */
  function copySpecSheet() {
    const ctx = brandContext();
    const lines = [
      `${SITE_NAME} — image specifications`,
      '',
      ...[...grouped.entries()].flatMap(([surface, items]) => [
        `## ${SURFACE_LABELS[surface]}`,
        ...items.map(({ spec, state }) => {
          const bits = [
            `- ${spec.label}: ${spec.width}×${spec.height} ${spec.format.toUpperCase()}`,
            spec.required ? '(required)' : '',
            `— ${spec.usage}.`,
            spec.notes ?? '',
            `[${STATE_META[state].label}]`,
          ];
          return bits.filter(Boolean).join(' ');
        }),
        '',
      ]),
      `Generated for ${ctx.siteName}.`,
    ];
    navigator.clipboard
      .writeText(lines.join('\n'))
      .then(() => addToast('Spec sheet copied — paste it anywhere', 'success'))
      .catch(() => addToast('Could not copy to clipboard', 'error'));
  }

  return (
    <div className="max-w-[1000px] mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <ListChecks className="h-7 w-7 text-gray-400" aria-hidden="true" />
            Design Checklist
          </h1>
          <p className="text-gray-600 mt-1">
            Every image this site uses, the size it wants, and whether it is set. Uploads are
            re-encoded automatically, so supply the largest clean original you have.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Hide unused slots' : 'Show all slots'}
          </Button>
          <Button variant="secondary" onClick={copySpecSheet}>
            <Copy className="h-4 w-4 mr-2" aria-hidden="true" />
            Copy spec sheet
          </Button>
        </div>
      </div>

      {missing > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong>{missing}</strong> required {missing === 1 ? 'image is' : 'images are'} not set
          yet. Those are the ones visitors notice.
        </div>
      )}

      <div className="space-y-6">
        {[...grouped.entries()].map(([surface, items]) => (
          <section key={surface}>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              {SURFACE_LABELS[surface]}
            </h2>
            <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
              {items.map(({ spec, state }) => {
                const { label, className, Icon, help } = STATE_META[state];
                return (
                  <li key={spec.key} className="flex flex-wrap items-start gap-3 px-4 py-3">
                    <Icon
                      className={`h-5 w-5 shrink-0 mt-0.5 ${className}`}
                      aria-hidden="true"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="font-medium text-gray-900">{spec.label}</span>
                        <span className="font-mono text-xs text-gray-500">
                          {spec.width}×{spec.height} {spec.format.toUpperCase()}
                        </span>
                        {spec.required && (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                            required
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-0.5">{spec.usage}</p>
                      {spec.notes && (
                        <p className="text-sm text-gray-500 mt-1 italic">{spec.notes}</p>
                      )}
                    </div>
                    <span className={`flex shrink-0 items-center gap-1 text-xs font-medium ${className}`}>
                      {label}
                      <Tooltip content={help} />
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
